import { useCallback, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { useAreaSelection } from '~/hooks/useAreaSelection'
import { useDayNavigation } from '~/hooks/useDayNavigation'
import type {
  GenSeriesKey,
  GenSeriesVisibility,
} from '~/pages/BalanceGeneration/constants'
import { DEFAULT_GEN_SERIES_VISIBILITY } from '~/pages/BalanceGeneration/constants'

type FilterState = {
  displayDate: Date
  seriesVisibility: GenSeriesVisibility
  visibleAreaCodes: Set<AreaCode>
  /**
   * エリアセレクターの選択肢 (= 対象エリア ∩ 業務エリア設定、issue #7535 /
   * conventions.md「業務エリアフィルタの原則」)。呼び出し元
   * `BalanceGenerationPage` が個人設定 (`useGetPersonalSettingsQuery`) から
   * 計算して渡す。取得中・エラー時のフォールバックも呼び出し元の責務 (この
   * フックは受け取った値をそのまま使う)。
   */
  selectableAreaCodes: readonly AreaCode[]
}

type FilterActions = {
  setDisplayDate: (date: Date) => void
  goToToday: () => void
  toggleSeries: (key: GenSeriesKey) => void
  setVisibleAreaCodes: (codes: Set<AreaCode>) => void
  /**
   * フィルタ群の指定だけを初期値へ戻す (「クリア」ボタン)。 表示日とグラフ配置は
   * 戻さない (需要側 `BalanceDemand/hooks/useFilterState` の同名 JSDoc と同じ理由)。
   */
  clearFilters: () => void
}

export type UseGenFilterStateReturn = FilterState & FilterActions

/**
 * 同時同量 - 発電のエリア選択の保存キー (AC-BDH-003、issue #7606)。 レイアウトの保存値
 * (`clearGridCardLayouts`) と違ってログアウトで消さないのは、 中身が組織に依らない
 * エリアコードだけで、 次のログインユーザーに見えても困る情報を含まないため。
 */
export const AREA_SELECTION_STORAGE_KEY = 'ebalance-hatsu-area-selection-v1'

export function useFilterState(
  selectableAreaCodes: readonly AreaCode[],
): UseGenFilterStateReturn {
  const { displayDate, setDisplayDate, goToToday } = useDayNavigation()
  const [seriesVisibility, setSeriesVisibility] = useState<GenSeriesVisibility>(
    DEFAULT_GEN_SERIES_VISIBILITY,
  )

  // AC-BDH-003 の選択状態遷移は需要・発電で共有の `useAreaSelection` に
  // 統合済み (review 指摘 A-1/A-2。需要側 `BalanceDemand/hooks/useFilterState`
  // と同じ理由)。
  const {
    selectableAreaCodes: stableSelectableAreaCodes,
    visibleAreaCodes,
    setVisibleAreaCodes,
    resetAreaSelection,
  } = useAreaSelection(selectableAreaCodes, AREA_SELECTION_STORAGE_KEY)

  const toggleSeries = useCallback((key: GenSeriesKey): void => {
    setSeriesVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // 初期値は宣言箇所のものをそのまま使い直す (ここで別の既定を書くと、
  // 「クリアした状態」 と 「開いた直後の状態」 が静かにズレる)。エリアは
  // `resetAreaSelection` に委譲する — `visibleAreaCodes` の導出が現在の
  // `selectableAreaCodes` (AC-BDH-003 の初期値) を自動で使う。
  const clearFilters = useCallback((): void => {
    setSeriesVisibility(DEFAULT_GEN_SERIES_VISIBILITY)
    resetAreaSelection()
  }, [resetAreaSelection])

  return {
    displayDate,
    seriesVisibility,
    visibleAreaCodes,
    selectableAreaCodes: stableSelectableAreaCodes,
    setDisplayDate,
    goToToday,
    toggleSeries,
    setVisibleAreaCodes,
    clearFilters,
  }
}
